import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import {
  mapContactMergeCandidate,
  mapContactMergeRecognition,
  mapContactMergeTaskDetail,
  mapContactMergeTaskPage,
  type ContactMergeCandidateRaw,
  type ContactMergeRecognitionRaw,
  type ContactMergeTaskDetailRaw,
  type ContactMergeTaskPageRaw,
} from "../api/adapters";
import type {
  ContactMergeActionPayload,
  ContactMergeCandidate,
  ContactMergeCandidateSearch,
  ContactMergeEditorPayload,
  ContactMergeListParams,
  ContactMergeMetadata,
  ContactMergeRecognition,
  ContactMergeTaskDetail,
  ContactMergeTaskPage,
} from "../types";

/** 读取联系人合并任务列表。 */
export async function fetchContactMergeTasks(
  params: ContactMergeListParams,
): Promise<ContactMergeTaskPage> {
  const response = await httpClient.get<ApiEnvelope<ContactMergeTaskPageRaw>>(
    "/crm/contact-merges",
    {
      params: {
        page: params.page,
        page_size: params.pageSize,
        status: params.status,
        keyword: params.keyword,
      },
    },
  );
  return mapContactMergeTaskPage(requireData(response.data, "任务列表"));
}

/** 读取任务详情。 */
export async function fetchContactMergeTask(taskId: number): Promise<ContactMergeTaskDetail> {
  const response = await httpClient.get<ApiEnvelope<ContactMergeTaskDetailRaw>>(
    `/crm/contact-merges/${taskId}`,
  );
  return mapContactMergeTaskDetail(requireData(response.data, "任务详情"));
}

/** 按学校和姓名片段搜索候选联系人。 */
export async function fetchContactMergeCandidates(
  search: ContactMergeCandidateSearch,
): Promise<ContactMergeCandidate[]> {
  const response = await httpClient.get<ApiEnvelope<ContactMergeCandidateRaw[]>>(
    "/crm/contact-merges/candidates",
    {
      params: {
        school_name: search.schoolName,
        contact_name: search.contactName,
        phone: search.phone,
        limit: 50,
      },
    },
  );
  return requireData(response.data, "候选联系人").map(mapContactMergeCandidate);
}

/** 识别粘贴的联系人截图。 */
export async function recognizeContactMergeImage(file: File): Promise<ContactMergeRecognition> {
  const response = await httpClient.post<ApiEnvelope<ContactMergeRecognitionRaw>>(
    "/crm/contact-merges/recognize",
    file,
    {
      headers: { "Content-Type": file.type },
      timeout: 45_000,
      "axios-retry": { retries: 0 },
    },
  );
  return mapContactMergeRecognition(requireData(response.data, "截图识别"));
}

/** 创建联系人合并任务。 */
export async function createContactMergeTask(
  payload: ContactMergeEditorPayload,
  recognition?: ContactMergeRecognition,
): Promise<ContactMergeTaskDetail> {
  const response = await httpClient.post<ApiEnvelope<ContactMergeTaskDetailRaw>>(
    "/crm/contact-merges",
    toTaskBody(payload, recognition),
  );
  return mapContactMergeTaskDetail(requireData(response.data, "创建任务"));
}

/** 更新联系人合并任务。 */
export async function updateContactMergeTask(
  taskId: number,
  version: number,
  payload: ContactMergeEditorPayload,
): Promise<ContactMergeTaskDetail> {
  const response = await httpClient.put<ApiEnvelope<ContactMergeTaskDetailRaw>>(
    `/crm/contact-merges/${taskId}`,
    { ...toTaskBody(payload), version },
  );
  return mapContactMergeTaskDetail(requireData(response.data, "更新任务"));
}

/** 执行预览、审批、执行、重试或异常处理动作。 */
export async function runContactMergeAction(
  taskId: number,
  action: string,
  payload: ContactMergeActionPayload,
): Promise<ContactMergeTaskDetail> {
  const requiresIdempotency = ["execute", "execute-direct", "retry"].includes(action);
  const response = await httpClient.post<ApiEnvelope<ContactMergeTaskDetailRaw>>(
    `/crm/contact-merges/${taskId}/${action}`,
    {
      version: payload.version,
      preview_digest: payload.previewDigest,
      reason: payload.reason,
      confirm_conflicts: payload.confirmConflicts ?? false,
    },
    requiresIdempotency
      ? { headers: { "Idempotency-Key": createIdempotencyKey(taskId, action) } }
      : undefined,
  );
  return mapContactMergeTaskDetail(requireData(response.data, `任务动作 ${action}`));
}

/** 读取联系人合并元数据。 */
export async function fetchContactMergeMetadata(): Promise<ContactMergeMetadata> {
  const response = await httpClient.get<
    ApiEnvelope<{ execute_enabled: boolean; image_max_bytes: number }>
  >("/crm/contact-merges/metadata");
  const raw = requireData(response.data, "页面元数据");
  return {
    executeEnabled: raw.execute_enabled,
    imageMaxBytes: raw.image_max_bytes,
  };
}

/** 将编辑器载荷转换为后端任务契约。 */
function toTaskBody(
  payload: ContactMergeEditorPayload,
  recognition?: ContactMergeRecognition,
) {
  return {
    target_contact_id: payload.targetContactId,
    source_contact_ids: payload.sourceContactIds,
    primary_sales_user_id: payload.primarySalesUserId,
    field_resolution: Object.fromEntries(
      Object.entries(payload.fieldResolution).map(([key, value]) => [toBackendField(key), value]),
    ),
    field_custom_values: Object.fromEntries(
      Object.entries(payload.fieldCustomValues ?? {}).map(([key, value]) => [
        toBackendField(key),
        value,
      ]),
    ),
    confirm_empty_overwrite_fields: (payload.confirmEmptyOverwriteFields ?? []).map(
      toBackendField,
    ),
    source_type: recognition ? "screenshot" : "manual",
    recognition_snapshot: recognition
      ? {
          school_name: recognition.schoolName,
          contact_names: recognition.contactNames,
        }
      : undefined,
    source_image_sha256: recognition?.imageSha256,
  };
}

/** 将前端字段键转换为后端字段键。 */
function toBackendField(field: string): string {
  return (
    {
      weixinCode: "weixin_code",
      collegeDepartmentMajorId: "college_department_major_id",
      departMajorName: "depart_major_name",
    }[field] ?? field
  );
}

/** 为执行和重试生成请求幂等键。 */
function createIdempotencyKey(taskId: number, action: string): string {
  return `${action}-${taskId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** 从统一响应中读取有效数据。 */
function requireData<T>(envelope: ApiEnvelope<T>, label: string): T {
  if (envelope.code !== "OK" || envelope.data === undefined || envelope.data === null) {
    throw new Error(`${label}接口返回了无效数据`);
  }
  return envelope.data;
}
